import { useEffect, useMemo, useState } from "react"

import LoadingOverlay from "./LoadingOverlay/LoadingOverlay"
import { ReservableChecker } from "./ReservableSlots.common.logic"
import { SevenDaysReservableSlotsChunks } from "./ReservableSlots.logic"
import styles from "./ReservableSlots.module.scss"
import { getDayColor, getMMDD, useWeekNavigation } from "./time"

import { useEvent } from "@/public/event"
import { GetSevenDaysReservableSlotsChunksQuery } from "@/public/gql/__generated__/graphql"
import { getJstWeekDay, newDateByJstParams } from "@/utils/date"
import { getJstDate, getJstFullYear, getJstMonth } from "@/utils/jst_date"

type Props = {
	loading: boolean
	maskedChunks: GetSevenDaysReservableSlotsChunksQuery["sevenDaysReservableSlotsChunks"]["chunks"]
	currentStaffID: string | null
	today: Date
	durationMinute: number
	sevenDaysFrom: Date
	isMenuSelected: boolean
	forceEmptyWhenCannotReserveCanceledReservationMenuSet: boolean
	setSevenDaysFrom: (fromDate: Date) => void
	onSelectSlot: (startTime: Date) => void
	loadChunksFrom: (fromDate: Date) => void
}

const ReservableSlots = ({
	loading,
	maskedChunks,
	today,
	currentStaffID,
	durationMinute,
	onSelectSlot,
	loadChunksFrom,
	sevenDaysFrom,
	setSevenDaysFrom,
	isMenuSelected,
	forceEmptyWhenCannotReserveCanceledReservationMenuSet,
}: Props) => {
	const event = useEvent()

	const [
		sevenDateAndReservableSlotsChunks,
		setSevenDateAndReservableSlotsChunks,
	] = useState<SevenDaysReservableSlotsChunks>(
		new SevenDaysReservableSlotsChunks(today, maskedChunks)
	)

	useEffect(() => {
		setSevenDateAndReservableSlotsChunks(
			new SevenDaysReservableSlotsChunks(sevenDaysFrom, maskedChunks)
		)
	}, [sevenDaysFrom, maskedChunks])

	const sevenDays = sevenDateAndReservableSlotsChunks.sevenDaysChunks.map(
		({ date }) => date
	)

	const { nextSevenDays, prevSevenDays } = useWeekNavigation(
		sevenDays,
		loadChunksFrom,
		setSevenDaysFrom,
		event.clickPrevSevenDays,
		event.clickNextSevenDays
	)

	// useMemoを使って予約可能なスロットがあるかどうかを計算
	const showNonReservableWarning = useMemo(() => {
		// ローディング中は予約可能として表示（警告を出さない）
		if (loading) {
			return false
		}

		const startTimes = sevenDateAndReservableSlotsChunks.getStartTimes()

		// everyメソッドを使用して全てが予約不可能なスロットであるかチェック
		return startTimes.every((startTime) =>
			sevenDays.every((date) => {
				const slots = sevenDateAndReservableSlotsChunks.getSlotsByDateAndStaff(
					date,
					currentStaffID
				)
				const checker = new ReservableChecker(
					slots,
					isMenuSelected,
					forceEmptyWhenCannotReserveCanceledReservationMenuSet
				)

				return !checker.isReservable(startTime, durationMinute)
			})
		)
	}, [
		sevenDateAndReservableSlotsChunks,
		sevenDays,
		currentStaffID,
		durationMinute,
		isMenuSelected,
		forceEmptyWhenCannotReserveCanceledReservationMenuSet,
		loading,
	])

	return (
		<div className={styles.reservable_slots}>
			<LoadingOverlay show={loading} message="予約可能日時を取得中です..." />
			{/* 予約不可でありローディング中でない場合に警告を表示 */}
			{showNonReservableWarning && (
				<div className={styles.no_reservation_warning}>
					このフォームからのご予約ができないため、
					<br />
					直接お店にお電話ください
				</div>
			)}
			<div className={styles.header}>再予約日時を選択 （クリック）</div>
			<div className={styles.week_selector}>
				<button className={styles.selector} onClick={prevSevenDays}>
					◀ 前の週
				</button>
				<button className={styles.selector} onClick={nextSevenDays}>
					次の週 ▶
				</button>
			</div>

			<div className={styles.table_container}>
				<table className={styles.reservable_slots_table}>
					<thead>
						<th></th>
						{sevenDays.map((date) => {
							return (
								<th key={date.toString()} style={{ color: getDayColor(date) }}>
									<div>{getMMDD(date)}</div>
									<div>{getJstWeekDay(date)}</div>
								</th>
							)
						})}
					</thead>
					<tbody>
						{sevenDateAndReservableSlotsChunks
							.getStartTimes()
							.map((startTime, i) => {
								return (
									<tr key={i}>
										<td>{startTime.toString()}</td>
										{sevenDays.map((date, j) => {
											const slots =
												sevenDateAndReservableSlotsChunks.getSlotsByDateAndStaff(
													date,
													currentStaffID
												)
											const checker = new ReservableChecker(
												slots,
												isMenuSelected,
												forceEmptyWhenCannotReserveCanceledReservationMenuSet
											)

											return (
												<td>
													{checker.isReservable(startTime, durationMinute) ? (
														<button
															className={styles.reservable_slot}
															onClick={() => {
																const selectedTime = newDateByJstParams(
																	getJstFullYear(date),
																	getJstMonth(date),
																	getJstDate(date),
																	startTime.hour,
																	startTime.minute
																)
																event.clickRereservableSlot(
																	selectedTime,
																	currentStaffID || ""
																)
																onSelectSlot(selectedTime)
															}}
														>
															️◎
														</button>
													) : (
														<div className={styles.not_reservable_slot}>✗</div>
													)}
												</td>
											)
										})}
									</tr>
								)
							})}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default ReservableSlots
